import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Award, Eye, Save, Sparkles, RefreshCw, Download, BarChart3, Search, Trophy, Users, AlertTriangle, Printer, FileSpreadsheet, SlidersHorizontal } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, InlineSpinner, Card } from '../../components/school/ui';
import { ReportCardView } from '../../components/school/ReportCardView';
import { DynamicChart } from '../../components/school/DynamicChart';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const ALL_SECTIONS = '__all__';

const MENTION_LABELS = {
  fr: { tres_bien: 'Très Bien', bien: 'Bien', assez_bien: 'Assez Bien', passable: 'Passable', insuffisant: 'Insuffisant' },
  pt: { tres_bien: 'Muito Bom', bien: 'Bom', assez_bien: 'Suficiente', passable: 'Aprovado', insuffisant: 'Insuficiente' },
};
const mentionLabel = (card) => (card.mention ? (MENTION_LABELS[card.language] || MENTION_LABELS.fr)[card.mention] || card.mention : '—');

const emptyRemark = { teacher_observation: '', direction: '', council: '', decision: '' };

export default function ReportCardsPage() {
  const { t } = useT();
  const orgId = useOrgId();
  const { selectedYearId } = useAcademicYear();
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [scope, setScope] = useState('term'); // 'term' | 'general'
  const [examId, setExamId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState(ALL_SECTIONS);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // remarks editor
  const [remark, setRemark] = useState(emptyRemark);
  const [savingRemark, setSavingRemark] = useState(false);

  // AI analysis
  const [aiOpen, setAiOpen] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLang, setAiLang] = useState('en');
  const [exporting, setExporting] = useState(false);
  // Class grades summary report
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [sumSearch, setSumSearch] = useState('');
  const [sumFilter, setSumFilter] = useState('all'); // all | passing | failing
  const [sumSort, setSumSort] = useState('rank'); // rank | name | average
  const pdfRef = React.useRef(null);
  const summaryRef = React.useRef(null);
  const [exportingSummary, setExportingSummary] = useState(false);
  const [exportingTermo, setExportingTermo] = useState(false);
  // Termo column mapping
  const [termoMapOpen, setTermoMapOpen] = useState(false);
  const [termoMapLoading, setTermoMapLoading] = useState(false);
  const [termoMapSaving, setTermoMapSaving] = useState(false);
  const [termoSubjects, setTermoSubjects] = useState([]);
  const [termoGroups, setTermoGroups] = useState([]); // unique subjects by name
  const [termoMapValues, setTermoMapValues] = useState({}); // subject NAME -> column|IGNORE
  const [termoColumns, setTermoColumns] = useState(['PORT', 'MAT', 'C.INT', 'FRAN', 'EXP']);
  const [termoImported, setTermoImported] = useState(false);
  const [termoTemplateName, setTermoTemplateName] = useState(null);
  const [termoImporting, setTermoImporting] = useState(false);
  const termoFileInputRef = useRef(null);
  const aiPollRef = React.useRef(null);

  React.useEffect(() => () => { if (aiPollRef.current) clearInterval(aiPollRef.current); }, []);
  const genSeq = React.useRef(0);

  const init = useCallback(async () => {
    if (!orgId) return;
    const [ex, cls] = await Promise.all([
      schoolApi.listExams(orgId, { academic_year_id: selectedYearId }),
      schoolApi.listClasses(orgId, selectedYearId),
    ]);
    setExams(ex); setClasses(cls);
    // Clear selections that may belong to a different year
    setExamId(''); setClassId(''); setSectionId(ALL_SECTIONS); setCards([]);
  }, [orgId, selectedYearId]);
  useEffect(() => { init(); }, [init]);
  useEffect(() => { if (classId) schoolApi.listSections(orgId, classId, selectedYearId).then(setSections); else setSections([]); setSectionId(ALL_SECTIONS); }, [orgId, classId, selectedYearId]);

  const fetchCards = useCallback(async () => {
    const ready = classId && (scope === 'general' || examId);
    if (!ready) return [];
    const params = {
      organization_id: orgId,
      class_id: classId,
      section_id: sectionId === ALL_SECTIONS ? undefined : sectionId,
      exam_id: scope === 'term' ? examId : undefined,
      all_terms: scope === 'general',
    };
    return schoolApi.getBatchReport(params);
  }, [orgId, classId, sectionId, examId, scope]);

  const generate = useCallback(async () => {
    const ready = classId && (scope === 'general' || examId);
    if (!ready) { setCards([]); return; }
    const seq = ++genSeq.current;
    setLoading(true);
    try {
      const res = await fetchCards();
      if (seq === genSeq.current) setCards(res); // ignore stale/out-of-order responses
    }
    catch (e) { if (seq === genSeq.current) { toast.error('Failed to generate'); setCards([]); } }
    finally { if (seq === genSeq.current) setLoading(false); }
  }, [fetchCards, classId, scope, examId]);

  useEffect(() => { generate(); }, [generate]);

  const openCard = (c) => {
    setSelected(c);
    setRemark({
      teacher_observation: c.remarks?.teacher_observation || '',
      direction: c.remarks?.direction || '',
      council: c.remarks?.council || '',
      decision: c.remarks?.decision || '',
    });
  };

  const saveRemarks = async () => {
    if (!selected) return;
    setSavingRemark(true);
    try {
      await schoolApi.saveReportRemark({
        organization_id: orgId, exam_id: selected.exam_id, student_id: selected.student_id,
        teacher_observation: remark.teacher_observation || null,
        direction: remark.direction || null,
        council: remark.council || null,
        decision: remark.decision || null,
      });
      // refresh the batch so the edited bulletin reflects saved observations/decision
      const fresh = await fetchCards();
      setCards(fresh);
      const updated = fresh.find((x) => x.student_id === selected.student_id);
      if (updated) setSelected(updated);
      toast.success(t('common.saved', 'Saved'));
    } catch (e) { toast.error('Failed to save'); }
    finally { setSavingRemark(false); }
  };

  const showCouncil = selected?.bulletin_format === 'general_secondary' || selected?.is_general;
  const notReady = !classId || (scope === 'term' && !examId);

  const aiParams = () => ({
    organization_id: orgId,
    class_id: classId,
    section_id: sectionId === ALL_SECTIONS ? undefined : sectionId,
    exam_id: scope === 'term' ? examId : undefined,
    all_terms: scope === 'general',
  });

  const openSummary = async () => {
    setSummaryOpen(true);
    setSummaryData(null);
    setSummaryLoading(true);
    setSumSearch(''); setSumFilter('all'); setSumSort('rank');
    try {
      const data = await schoolApi.classGradesSummary(aiParams());
      setSummaryData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('rc.summaryFailed', 'Failed to load class summary'));
    } finally {
      setSummaryLoading(false);
    }
  };

  const summaryRows = React.useMemo(() => {
    if (!summaryData) return [];
    let rows = [...(summaryData.rows || [])];
    const q = sumSearch.trim().toLowerCase();
    if (q) rows = rows.filter((r) => (r.name || '').toLowerCase().includes(q));
    if (sumFilter === 'passing') rows = rows.filter((r) => r.is_pass);
    else if (sumFilter === 'failing') rows = rows.filter((r) => !r.is_pass);
    rows.sort((a, b) => {
      if (sumSort === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sumSort === 'average') return (b.average ?? -1) - (a.average ?? -1);
      return (a.rank ?? 9999) - (b.rank ?? 9999);
    });
    return rows;
  }, [summaryData, sumSearch, sumFilter, sumSort]);

  const fmtScore = (v) => (v === null || v === undefined ? '—' : v);

  const exportSummaryPdf = async () => {
    const el = summaryRef.current;
    if (!el || !summaryData) return;
    setExportingSummary(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const safe = (s) => (s || 'class').toString().replace(/[^\w\-]+/g, '-').replace(/-+/g, '-');
      const scopeLabel = summaryData.exam_name || (summaryData.is_general ? 'General' : '');
      const filename = `Class-Grades-Summary_${safe(summaryData.class_name)}${scopeLabel ? '_' + safe(scopeLabel) : ''}.pdf`;
      await html2pdf().set({
        margin: [8, 8, 10, 8],
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 1400 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(el).save();
      toast.success(t('rc.pdfDone', 'PDF exported'));
    } catch (e) {
      toast.error(t('rc.pdfFailed', 'Failed to export PDF'));
    } finally { setExportingSummary(false); }
  };

  const exportTermoXlsx = async () => {
    if (!classId) { toast.error(t('rc.selectClassFirst', 'Please select a class first')); return; }
    setExportingTermo(true);
    try {
      const blob = await schoolApi.classGradesSummaryTermoXlsx({
        organization_id: orgId,
        class_id: classId,
        section_id: sectionId === ALL_SECTIONS ? undefined : sectionId,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (s) => (s || 'class').toString().replace(/[^\w\-]+/g, '-').replace(/-+/g, '-');
      const cname = summaryData?.class_name || cards?.[0]?.class_name || 'class';
      a.download = `TERMO_${safe(cname)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t('rc.termoDone', 'Termo Excel exported'));
    } catch (e) {
      let msg = t('rc.termoFailed', 'Failed to export Termo');
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          msg = JSON.parse(txt).detail || msg;
        }
      } catch (_) { /* ignore */ }
      toast.error(msg);
    } finally { setExportingTermo(false); }
  };

  const openTermoMapping = async () => {
    if (!classId) { toast.error(t('rc.selectClassFirst', 'Please select a class first')); return; }
    setTermoMapOpen(true);
    setTermoMapLoading(true);
    try {
      const data = await schoolApi.getTermoMapping({ organization_id: orgId, class_id: classId });
      const subs = data?.subjects || [];
      setTermoSubjects(subs);
      setTermoColumns(data?.columns || ['PORT', 'MAT', 'C.INT', 'FRAN', 'EXP']);
      setTermoImported(!!data?.imported);
      setTermoTemplateName(data?.template_name || null);
      // Group identical subject names (same subject repeated across sections) so the
      // user maps once per subject; the choice is applied to all matching subject IDs.
      const groupMap = {};
      subs.forEach((s) => {
        const key = s.name || '(unnamed)';
        if (!groupMap[key]) groupMap[key] = { name: key, ids: [], mapped: null, suggestion: null };
        groupMap[key].ids.push(s.subject_id);
        if (s.mapped && !groupMap[key].mapped) groupMap[key].mapped = s.mapped;
        if (s.suggestion && !groupMap[key].suggestion) groupMap[key].suggestion = s.suggestion;
      });
      const groups = Object.values(groupMap).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      setTermoGroups(groups);
      // Prefill per unique subject: saved mapping -> keyword suggestion -> IGNORE
      const values = {};
      groups.forEach((g) => { values[g.name] = g.mapped || g.suggestion || 'IGNORE'; });
      setTermoMapValues(values);
    } catch (e) {
      toast.error(t('rc.termoMapLoadFailed', 'Failed to load subject mapping'));
      setTermoMapOpen(false);
    } finally { setTermoMapLoading(false); }
  };

  const handleTermoTemplateFile = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = '';  // allow re-selecting the same file
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) { toast.error(t('rc.termoNeedXlsx', 'Please choose a .xlsx file')); return; }
    setTermoImporting(true);
    try {
      const fd = new FormData();
      fd.append('organization_id', orgId);
      fd.append('class_id', classId);
      fd.append('file', f);
      const res = await schoolApi.importTermoTemplate(fd);
      toast.success(`${t('rc.termoImported', 'Template imported')}: ${(res.columns || []).join(', ')}`);
      await openTermoMapping();  // refresh columns + suggestions
    } catch (err) {
      const detail = err?.response?.data?.detail || t('rc.termoImportFailed', 'Failed to import template');
      toast.error(detail);
    } finally { setTermoImporting(false); }
  };

  const saveTermoMapping = async () => {
    setTermoMapSaving(true);
    try {
      // Expand the per-name choices back to every subject ID.
      const mapping = {};
      termoSubjects.forEach((s) => { mapping[s.subject_id] = termoMapValues[s.name] || 'IGNORE'; });
      await schoolApi.saveTermoMapping({ organization_id: orgId, class_id: classId, mapping });
      toast.success(t('rc.termoMapSaved', 'Subject mapping saved'));
      setTermoMapOpen(false);
    } catch (e) {
      toast.error(t('rc.termoMapSaveFailed', 'Failed to save mapping'));
    } finally { setTermoMapSaving(false); }
  };


  const printSummary = () => {
    const el = summaryRef.current;
    if (!el || !summaryData) return;
    const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
      .map((n) => n.outerHTML).join('');
    const w = window.open('', '_blank', 'width=1200,height=800');
    if (!w) { toast.error(t('rc.popupBlocked', 'Please allow pop-ups to print')); return; }
    w.document.write(`<!DOCTYPE html><html><head><title>${t('rc.classSummary', 'Class Grades Summary')}</title>${styles}` +
      `<style>@page{size:A4 landscape;margin:10mm;} body{padding:12px;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;font-family:Figtree,system-ui,sans-serif;}</style>` +
      `</head><body>${el.outerHTML}</body></html>`);
    w.document.close();
    const trigger = () => { w.focus(); w.print(); };
    if (w.document.readyState === 'complete') setTimeout(trigger, 600);
    else w.onload = () => setTimeout(trigger, 600);
  };

  const openAi = async () => {
    setAiOpen(true);
    setAiData(null);
    try {
      const saved = await schoolApi.getClassAiAnalysis(aiParams());
      if (saved) {
        setAiData(saved);
        // Only resume polling if a generation is genuinely still in progress
        // (started within the last 5 minutes). Otherwise it's stale/stuck and
        // the footer "Generate" button below acts as a Retry.
        if (saved.status === 'processing') {
          const reqAt = saved.requested_at ? new Date(saved.requested_at).getTime() : 0;
          const isRecent = reqAt && (Date.now() - reqAt) < 5 * 60 * 1000;
          if (isRecent) pollAi();
        }
      }
    } catch (e) { /* no saved analysis yet */ }
  };

  const pollAi = () => {
    if (aiPollRef.current) clearInterval(aiPollRef.current);
    setAiLoading(true);
    let errors = 0;
    let ticks = 0;
    const MAX_TICKS = 80;        // ~4 min safety cap (80 * 3s)
    const MAX_ERRORS = 5;
    aiPollRef.current = setInterval(async () => {
      ticks += 1;
      if (ticks > MAX_TICKS) {
        clearInterval(aiPollRef.current);
        setAiLoading(false);
        toast.error(t('rc.aiFailed', 'AI analysis timed out. Please try again.'));
        return;
      }
      try {
        const doc = await schoolApi.getClassAiAnalysis(aiParams());
        errors = 0;
        if (doc && doc.status === 'completed') {
          clearInterval(aiPollRef.current);
          setAiData(doc);
          setAiLoading(false);
          toast.success(t('rc.aiDone', 'Analysis ready'));
        } else if (doc && doc.status === 'failed') {
          clearInterval(aiPollRef.current);
          setAiLoading(false);
          toast.error(doc.error || t('rc.aiFailed', 'AI analysis failed'));
        }
        // else still processing -> keep polling
      } catch (e) {
        errors += 1;
        if (errors >= MAX_ERRORS) {
          clearInterval(aiPollRef.current);
          setAiLoading(false);
          toast.error(t('rc.aiFailed', 'AI analysis failed'));
        }
      }
    }, 3000);
  };

  const runAi = async () => {
    setAiLoading(true);
    try {
      const res = await schoolApi.generateClassAiAnalysis(aiParams());
      setAiData(res);          // status: 'processing'
      pollAi();                // poll until completed/failed
    } catch (e) {
      setAiLoading(false);
      toast.error(e?.response?.data?.detail || t('rc.aiFailed', 'AI analysis failed'));
    }
  };

  const exportPdf = async () => {
    if (!aiData?.analysis) return;
    setExporting(true);
    try {
      // Mount the off-screen printable node, then wait for charts to render & measure.
      await new Promise((r) => setTimeout(r, 650));
      const el = pdfRef.current;
      if (!el) throw new Error('nothing to export');
      const html2pdf = (await import('html2pdf.js')).default;
      const safe = (s) => (s || 'class').toString().replace(/[^\w\-]+/g, '-').replace(/-+/g, '-');
      const filename = `AI-Analysis_${safe(aiData.class_name)}_${aiLang}.pdf`;
      await html2pdf().set({
        margin: [10, 10, 12, 10],
        filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff', windowWidth: 820 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      }).from(el).save();
      toast.success(t('rc.pdfDone', 'PDF exported'));
    } catch (e) {
      toast.error(t('rc.pdfFailed', 'Failed to export PDF'));
    } finally { setExporting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('nav.reportCards')} subtitle={t('rc.title', 'Bulletins')} icon={Award} />

      <Card className="p-4 mb-4">
        <Tabs value={scope} onValueChange={setScope} className="mb-3">
          <TabsList data-testid="rc-scope-tabs">
            <TabsTrigger value="term" data-testid="rc-scope-term">{t('rc.scopeTerm', 'Single Trimester')}</TabsTrigger>
            <TabsTrigger value="general" data-testid="rc-scope-general">{t('rc.scopeGeneral', 'General (All Terms)')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {scope === 'term' && (
            <Select value={examId} onValueChange={setExamId}>
              <SelectTrigger data-testid="rc-exam-select"><SelectValue placeholder={t('nav.exams')} /></SelectTrigger>
              <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="rc-class-select"><SelectValue placeholder={t('students.class')} /></SelectTrigger>
            <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sectionId} onValueChange={setSectionId} disabled={!classId}>
            <SelectTrigger data-testid="rc-section-select"><SelectValue placeholder={t('students.section')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_SECTIONS}>{t('rc.allSections', 'All sections')}</SelectItem>
              {sections.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {scope === 'general' && (
          <p className="text-xs text-muted-foreground mt-2" data-testid="rc-general-hint">{t('rc.generalHint', 'Aggregates all trimesters of the academic year into one cumulative General report.')}</p>
        )}
      </Card>

      <Card>
        {loading ? <InlineSpinner /> : notReady ? (
          <EmptyState icon={Award} title={t('rc.selectFilters', 'Select filters')} description={scope === 'general' ? t('rc.generalHint', '') : t('rc.selectFilters', '')} />
        ) : cards.length === 0 ? (
          <EmptyState icon={Award} title={t('common.noData')} description={t('rc.noMarks', 'No marks recorded for this selection')} />
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
              <p className="text-sm text-muted-foreground">{cards.length} {t('nav.students', 'students')}</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={openSummary} className="gap-2" data-testid="rc-summary-button">
                  <BarChart3 className="w-4 h-4" /> {t('rc.classSummary', 'Class Grades Summary')}
                </Button>
                <Button size="sm" variant="outline" onClick={exportTermoXlsx} disabled={exportingTermo} className="gap-2" data-testid="rc-termo-button">
                  {exportingTermo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} {t('rc.exportTermo', 'Export Termo (Excel)')}
                </Button>
                <Button size="sm" variant="outline" onClick={openTermoMapping} className="gap-2" data-testid="rc-termo-mapping-button" title={t('rc.termoMapping', 'Termo Mapping')}>
                  <SlidersHorizontal className="w-4 h-4" /> {t('rc.termoMapping', 'Termo Mapping')}
                </Button>
                <Button size="sm" onClick={openAi} className="gap-2" data-testid="rc-ai-button">
                  <Sparkles className="w-4 h-4" /> {t('rc.aiAnalysis', 'AI Analysis')}
                </Button>
              </div>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="px-4 py-3">{t('rc.rank')}</th>
                <th className="px-4 py-3">{t('common.name')}</th>
                <th className="px-4 py-3">{t('students.section')}</th>
                <th className="px-4 py-3 text-right">{t('rc.moyenne', 'Average /20')}</th>
                <th className="px-4 py-3 text-center">{t('rc.mention', 'Mention')}</th>
                <th className="px-4 py-3 text-right">{t('common.actions')}</th>
              </tr></thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.student_id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`rc-row-${c.student_id}`}>
                    <td className="px-4 py-3 font-semibold tabular-nums">#{c.rank}</td>
                    <td className="px-4 py-3 font-medium">{c.student_name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{c.section_name || '-'}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold" data-testid={`rc-moy-${c.student_id}`}>{c.moyenne_sur_20 != null ? `${c.moyenne_sur_20}/20` : '—'}</td>
                    <td className="px-4 py-3 text-center"><Badge variant="outline">{mentionLabel(c)}</Badge></td>
                    <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => openCard(c)} data-testid={`rc-view-${c.student_id}`}><Eye className="w-3.5 h-3.5 mr-1" /> {t('common.view')}</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="rc-view-dialog">
          <DialogTitle className="sr-only">{t('rc.reportCard', 'Report Card')}</DialogTitle>
          <DialogDescription className="sr-only">{t('rc.reportCardDesc', 'Student report card preview and observations')}</DialogDescription>
          {selected && (
            <>
              <ReportCardView card={selected} />

              {/* Observations & Decision editor (not printed) */}
              <div className="no-print mt-5 border-t border-border pt-4 space-y-3" data-testid="rc-remarks-editor">
                <p className="text-sm font-semibold">{t('rc.observations', 'Observations & Decision')}</p>
                {showCouncil && (
                  <div>
                    <Label className="text-xs">{t('rc.councilObs', 'Pedagogical Council')}</Label>
                    <Textarea rows={2} value={remark.council} onChange={(e) => setRemark({ ...remark, council: e.target.value })} data-testid="rc-council-input" />
                  </div>
                )}
                <div>
                  <Label className="text-xs">{t('rc.teacherObs', 'Class Teacher Observation')}</Label>
                  <Textarea rows={2} value={remark.teacher_observation} onChange={(e) => setRemark({ ...remark, teacher_observation: e.target.value })} data-testid="rc-teacher-obs-input" />
                </div>
                <div>
                  <Label className="text-xs">{t('rc.directionObs', 'Direction')}</Label>
                  <Textarea rows={2} value={remark.direction} onChange={(e) => setRemark({ ...remark, direction: e.target.value })} data-testid="rc-direction-input" />
                </div>
                <div>
                  <Label className="text-xs">{t('rc.decision', 'Decision')}</Label>
                  <Select value={remark.decision || 'auto'} onValueChange={(v) => setRemark({ ...remark, decision: v === 'auto' ? '' : v })}>
                    <SelectTrigger data-testid="rc-decision-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">{t('common.auto', 'Automatic (based on average)')}</SelectItem>
                      <SelectItem value="passe">{selected.language === 'pt' ? 'Passa de classe' : 'Passe en classe supérieure'}</SelectItem>
                      <SelectItem value="redouble">{selected.language === 'pt' ? 'Repete' : 'Redouble'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end">
                  <Button onClick={saveRemarks} disabled={savingRemark} className="gap-2" data-testid="rc-save-remarks">
                    <Save className="w-4 h-4" /> {savingRemark ? t('common.saving') : t('rc.saveRemarks', 'Save Observations')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Class Analysis dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="rc-ai-dialog">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[color:var(--eli-crest-navy,#1e3a5f)]" />
            <h3 className="text-lg font-semibold">{t('rc.aiAnalysis', 'AI Class Analysis')}</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            {aiData?.scope_label || t('rc.aiHint', 'AI-powered analysis of the whole class grade report.')}
            {aiData?.generated_at ? ` · ${new Date(aiData.generated_at).toLocaleString()}` : ''}
          </p>

          {aiData?.analysis ? (
            <Tabs value={aiLang} onValueChange={setAiLang} className="mt-2">
              <TabsList data-testid="rc-ai-lang-tabs">
                <TabsTrigger value="en" data-testid="rc-ai-en">English</TabsTrigger>
                <TabsTrigger value="fr" data-testid="rc-ai-fr">Français</TabsTrigger>
                <TabsTrigger value="pt" data-testid="rc-ai-pt">Português</TabsTrigger>
              </TabsList>

              {Array.isArray(aiData.charts) && aiData.charts.length > 0 && (
                <div className="mt-4" data-testid="rc-ai-charts">
                  <p className="text-sm font-semibold mb-3">{t('rc.aiCharts', 'Visual Insights')}</p>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {aiData.charts.map((ch, i) => (
                      <DynamicChart key={`${ch.dataset || ch.type}-${i}`} chart={ch} lang={aiLang} />
                    ))}
                  </div>
                </div>
              )}

              <p className="text-sm font-semibold mt-5 mb-2">{t('rc.aiSummary', 'Detailed Analysis')}</p>
              <div className="whitespace-pre-wrap text-sm leading-relaxed border border-border rounded-md p-4 bg-muted/30 max-h-[50vh] overflow-y-auto" data-testid="rc-ai-content">
                {aiData.analysis[aiLang] || aiData.analysis.en || ''}
              </div>
            </Tabs>
          ) : (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="rc-ai-empty">
              {aiLoading ? t('rc.aiGenerating', 'Analyzing the class… this can take up to a minute.') : t('rc.aiNone', 'No analysis yet. Generate one below.')}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setAiOpen(false)}>{t('common.close', 'Close')}</Button>
            {aiData?.analysis && (
              <Button variant="outline" onClick={exportPdf} disabled={exporting} className="gap-2" data-testid="rc-ai-export-pdf">
                {exporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {exporting ? t('rc.pdfExporting', 'Exporting…') : t('rc.exportPdf', 'Export PDF')}
              </Button>
            )}
            <Button onClick={runAi} disabled={aiLoading} className="gap-2" data-testid="rc-ai-generate">
              {aiLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {aiLoading ? t('rc.aiGenerating', 'Analyzing…') : (aiData?.analysis ? t('rc.aiRegenerate', 'Regenerate') : t('rc.aiGenerate', 'Generate Analysis'))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Termo column mapping */}
      <Dialog open={termoMapOpen} onOpenChange={(o) => { if (!termoMapSaving) setTermoMapOpen(o); }}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="rc-termo-mapping-dialog">
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-[color:var(--eli-crest-navy,#1e3a5f)]" />
            {t('rc.termoMapping', 'Termo Mapping')}
          </DialogTitle>
          <DialogDescription>
            {t('rc.termoMappingHint', 'Map each subject to a Termo column. Choose "Ignore" to exclude a subject. Multiple subjects on the same column are averaged.')}
          </DialogDescription>

          <div className="flex items-center justify-between gap-2 mt-1 flex-wrap">
            <p className="text-xs text-muted-foreground" data-testid="rc-termo-columns-info">
              {t('rc.termoColumns', 'Columns')}: <span className="font-medium text-foreground">{termoColumns.join(', ')}</span>
              {termoImported && termoTemplateName ? ` · ${t('rc.termoFrom', 'from')} ${termoTemplateName}` : ` · ${t('rc.termoDefault', 'default')}`}
            </p>
            <input ref={termoFileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleTermoTemplateFile} data-testid="rc-termo-import-input" />
            <Button size="sm" variant="outline" className="gap-2" disabled={termoImporting} onClick={() => termoFileInputRef.current?.click()} data-testid="rc-termo-import-button">
              {termoImporting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 rotate-180" />}
              {t('rc.termoImportXlsx', 'Import .xlsx')}
            </Button>
          </div>

          {termoMapLoading ? (
            <div className="py-10 flex justify-center"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : termoGroups.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="rc-termo-map-empty">
              {t('rc.termoMapNoSubjects', 'No subjects found for this class.')}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 max-h-[56vh] overflow-y-auto pr-1">
              {termoGroups.map((g) => (
                <div key={g.name} className="flex items-center justify-between gap-3 border border-border rounded-md px-3 py-2" data-testid={`rc-termo-map-row-${g.name}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" title={g.name}>{g.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {g.ids.length > 1 ? `${g.ids.length} ${t('rc.termoSections', 'sections')}` : ''}
                      {g.ids.length > 1 && g.suggestion ? ' · ' : ''}
                      {g.suggestion ? `${t('rc.termoSuggested', 'Suggested')}: ${g.suggestion}` : ''}
                    </p>
                  </div>
                  <Select
                    value={termoMapValues[g.name] || 'IGNORE'}
                    onValueChange={(v) => setTermoMapValues((prev) => ({ ...prev, [g.name]: v }))}
                  >
                    <SelectTrigger className="w-[130px] shrink-0" data-testid={`rc-termo-map-select-${g.name}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {termoColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      <SelectItem value="IGNORE">{t('rc.termoIgnore', 'Ignore')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setTermoMapOpen(false)} disabled={termoMapSaving} data-testid="rc-termo-map-cancel">{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={saveTermoMapping} disabled={termoMapSaving || termoMapLoading || termoGroups.length === 0} className="gap-2" data-testid="rc-termo-map-save">
              {termoMapSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.save', 'Save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Class Grades Summary report */}
      <Dialog open={summaryOpen} onOpenChange={setSummaryOpen}>
        <DialogContent className="max-w-[95vw] lg:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="rc-summary-dialog">
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[color:var(--eli-crest-navy,#1e3a5f)]" />
            {t('rc.classSummary', 'Class Grades Summary')}
          </DialogTitle>
          <DialogDescription>
            {summaryData ? `${summaryData.class_name || ''}${summaryData.exam_name ? ' · ' + summaryData.exam_name : (summaryData.is_general ? ' · ' + t('rc.scopeGeneral', 'General (All Terms)') : '')}` : t('common.loading', 'Loading...')}
          </DialogDescription>
          {summaryData && (
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={printSummary} className="gap-2" data-testid="rc-summary-print">
                <Printer className="w-4 h-4" />
                {t('rc.print', 'Print')}
              </Button>
              <Button size="sm" variant="outline" onClick={exportSummaryPdf} disabled={exportingSummary} className="gap-2" data-testid="rc-summary-export-pdf">
                {exportingSummary ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {t('rc.exportPdf', 'Export PDF')}
              </Button>
              <Button size="sm" variant="outline" onClick={exportTermoXlsx} disabled={exportingTermo} className="gap-2" data-testid="rc-summary-export-termo">
                {exportingTermo ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                {t('rc.exportTermo', 'Export Termo (Excel)')}
              </Button>
            </div>
          )}

          {summaryLoading ? <InlineSpinner /> : !summaryData ? null : (
            <div className="space-y-5" ref={summaryRef}>
              <div className="mb-1 border-b border-border pb-2">
                <h2 className="text-base font-bold">{t('rc.classSummary', 'Class Grades Summary')}</h2>
                <p className="text-xs text-muted-foreground">{summaryData.class_name}{summaryData.exam_name ? ' · ' + summaryData.exam_name : (summaryData.is_general ? ' · ' + t('rc.scopeGeneral', 'General (All Terms)') : '')}{summaryData.academic_year_name ? ' · ' + summaryData.academic_year_name : ''}</p>
              </div>
              {/* Summary cards */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="rounded-lg border border-border p-3" data-testid="sum-card-class-average">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><BarChart3 className="w-3.5 h-3.5" />{t('rc.classAverage', 'Class Average')}</div>
                  <p className="text-2xl font-bold tabular-nums mt-1">{summaryData.summary.class_average ?? '—'}<span className="text-sm font-normal text-muted-foreground">/{summaryData.summary.overall_scale}</span></p>
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="sum-card-pass-rate">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="w-3.5 h-3.5" />{t('rc.passRate', 'Pass Rate')}</div>
                  <p className="text-2xl font-bold tabular-nums mt-1">{summaryData.summary.pass_rate.percent}%</p>
                  <p className="text-[11px] text-muted-foreground">{summaryData.summary.pass_rate.passed}/{summaryData.summary.pass_rate.total}</p>
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="sum-card-top-student">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Trophy className="w-3.5 h-3.5" />{t('rc.topStudent', 'Top Student')}</div>
                  <p className="text-sm font-semibold mt-1 truncate" title={summaryData.summary.top_student?.name}>{summaryData.summary.top_student?.name || '—'}</p>
                  <p className="text-[11px] text-muted-foreground tabular-nums">{summaryData.summary.top_student?.average ?? ''}{summaryData.summary.top_student ? '/' + summaryData.summary.overall_scale : ''}</p>
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="sum-card-common-grade">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Award className="w-3.5 h-3.5" />{t('rc.commonGrade', 'Most Common Grade')}</div>
                  <p className="text-2xl font-bold mt-1">{summaryData.summary.most_common_grade?.grade || '—'}</p>
                  <p className="text-[11px] text-muted-foreground">{summaryData.summary.most_common_grade ? `${summaryData.summary.most_common_grade.count} ${t('nav.students', 'students')}` : ''}</p>
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="sum-card-failing">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><AlertTriangle className="w-3.5 h-3.5" />{t('rc.failingStudents', 'Failing Students')}</div>
                  <p className="text-2xl font-bold tabular-nums mt-1 text-[color:var(--destructive,#dc2626)]">{summaryData.summary.failing_count}</p>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-2" data-html2canvas-ignore="true">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={sumSearch} onChange={(e) => setSumSearch(e.target.value)}
                    placeholder={t('rc.searchStudent', 'Search student...')}
                    className="w-full h-9 pl-8 pr-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    data-testid="sum-search"
                  />
                </div>
                <Select value={sumFilter} onValueChange={setSumFilter}>
                  <SelectTrigger className="w-full sm:w-44" data-testid="sum-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('rc.allStudents', 'All students')}</SelectItem>
                    <SelectItem value="passing">{t('rc.passingOnly', 'Passing only')}</SelectItem>
                    <SelectItem value="failing">{t('rc.failingOnly', 'Failing only')}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sumSort} onValueChange={setSumSort}>
                  <SelectTrigger className="w-full sm:w-44" data-testid="sum-sort"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rank">{t('rc.sortRank', 'Sort by rank')}</SelectItem>
                    <SelectItem value="name">{t('rc.sortName', 'Sort by name')}</SelectItem>
                    <SelectItem value="average">{t('rc.sortAverage', 'Sort by average')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Matrix table */}
              <div className="overflow-x-auto border border-border rounded-lg">
                <table className="w-full text-sm" data-testid="sum-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                      <th className="px-3 py-2.5 sticky left-0 bg-muted/40">{t('rc.rank')}</th>
                      <th className="px-3 py-2.5">{t('common.name')}</th>
                      {summaryData.subjects.map((s) => (
                        <th key={s.subject_id} className="p-0 align-middle" style={{ height: '185px', width: '32px' }}>
                          <div style={{ position: 'relative', width: '32px', height: '185px', margin: '0 auto' }}>
                            <span
                              className="text-xs font-medium"
                              style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)', transformOrigin: 'center', whiteSpace: 'nowrap', maxWidth: '175px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              title={s.name}
                            >{s.name}</span>
                          </div>
                        </th>
                      ))}
                      <th className="px-3 py-2.5 text-center">{t('rc.average', 'Average')}</th>
                      <th className="px-3 py-2.5 text-center">{t('rc.grade', 'Grade')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((r) => (
                      <tr key={r.student_id} className={`border-b border-border last:border-0 ${!r.is_pass ? 'bg-[color:var(--destructive,#dc2626)]/5' : ''}`} data-testid={`sum-row-${r.student_id}`}>
                        <td className="px-3 py-2 tabular-nums sticky left-0 bg-background font-semibold">{r.rank}</td>
                        <td className="px-3 py-2 font-medium whitespace-nowrap">{r.name}</td>
                        {summaryData.subjects.map((s) => (
                          <td key={s.subject_id} className="px-2 py-2 text-center tabular-nums">{fmtScore(r.scores[s.subject_id])}</td>
                        ))}
                        <td className="px-3 py-2 text-center tabular-nums font-semibold">{r.average ?? '—'}</td>
                        <td className="px-3 py-2 text-center"><Badge variant={r.is_pass ? 'secondary' : 'destructive'} className="font-semibold">{r.grade}</Badge></td>
                      </tr>
                    ))}
                    {summaryRows.length === 0 && (
                      <tr><td colSpan={summaryData.subjects.length + 4} className="px-3 py-6 text-center text-muted-foreground">{t('common.noData', 'No data')}</td></tr>
                    )}
                  </tbody>
                  {/* Statistical footer */}
                  <tfoot className="border-t-2 border-border">
                    {['avg', 'min', 'max'].map((stat) => (
                      <tr key={stat} className="text-xs">
                        <td className="px-3 py-2 font-semibold uppercase text-muted-foreground sticky left-0 bg-background" colSpan={2}>
                          {stat === 'avg' ? t('rc.statAverage', 'Average') : stat === 'min' ? t('rc.statMin', 'Minimum') : t('rc.statMax', 'Maximum')}
                        </td>
                        {summaryData.subjects.map((s) => (
                          <td key={s.subject_id} className="px-2 py-2 text-center tabular-nums">{fmtScore(summaryData.footer.subjects[s.subject_id]?.[stat])}</td>
                        ))}
                        <td className="px-3 py-2 text-center tabular-nums font-semibold" colSpan={2}>{fmtScore(summaryData.footer.overall?.[stat])}</td>
                      </tr>
                    ))}
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setSummaryOpen(false)}>{t('common.close', 'Close')}</Button>
          </div>
        </DialogContent>
      </Dialog>
      {exporting && aiData?.analysis && (
        <div aria-hidden="true" style={{ position: 'absolute', left: '-99999px', top: 0 }}>
          <div ref={pdfRef} style={{ width: '760px', background: '#ffffff', color: '#0f172a', padding: '24px', fontFamily: 'Figtree, system-ui, sans-serif' }}>
            <div style={{ borderBottom: '2px solid #1e3a5f', paddingBottom: '10px', marginBottom: '16px' }}>
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e3a5f' }}>
                {t('rc.aiAnalysis', 'AI Class Analysis')} — {aiData.class_name || ''}
              </div>
              <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                {aiData.scope_label || ''}{aiData.generated_at ? ` · ${new Date(aiData.generated_at).toLocaleString()}` : ''}
              </div>
            </div>

            {Array.isArray(aiData.charts) && aiData.charts.length > 0 && (
              <>
                <div style={{ fontSize: '14px', fontWeight: 700, margin: '4px 0 10px' }}>{t('rc.aiCharts', 'Visual Insights')}</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '14px' }}>
                  {aiData.charts.map((ch, i) => (
                    <div key={`pdf-${ch.dataset || ch.type}-${i}`} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <DynamicChart chart={ch} lang={aiLang} animate={false} />
                    </div>
                  ))}
                </div>
              </>
            )}

            <div style={{ fontSize: '14px', fontWeight: 700, margin: '18px 0 8px' }}>{t('rc.aiSummary', 'Detailed Analysis')}</div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '12px', lineHeight: 1.6, border: '1px solid #e2e8f0', borderRadius: '6px', padding: '14px', background: '#f8fafc' }}>
              {aiData.analysis[aiLang] || aiData.analysis.en || ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
